/**
 * Un stage par semaine et par enfant (demande d'Éclosion, 30/10/2026).
 *
 * La page des stages mettait toutes les semaines d'une période dans une grille
 * plate, et rien n'empêchait de cocher deux stages de la même semaine pour le
 * même enfant — ni l'écran, ni le panier, ni `create_order`, qui ne refuse que
 * la MÊME session. En production : 122 paires d'inscriptions d'un même enfant
 * qui se recoupent depuis 2025, la plupart annulées ensuite (mesuré le
 * 30/10/2026). Voir `docs/ux/PROPOSITION-STAGES-PAR-SEMAINE.md`.
 *
 * Désormais les stages sont rangés par semaine, et une semaine se prend une
 * fois par enfant. Ce qui la prend, par ordre :
 *   1. une inscription qui tient la place (payée ou facture en attente, non
 *      annulée — une annulation seulement DEMANDÉE la tient encore), dans une
 *      session encore au catalogue ;
 *   2. une ligne du panier ;
 *   3. une demande d'inclusion en attente ;
 *   4. un choix en cours sur la page.
 * Les trois premières bloquent ; un choix en cours s'échange (« Choisir
 * celui-ci à la place »). VALIDÉ ÉCLOSION, 30/10/2026.
 *
 * « La même semaine », ce sont des dates qui se recoupent — la règle de
 * `listeAttente/semaine.ts` —, pas le libellé « S1 / S2 » : un séjour de deux
 * semaines, annoncé, recouvre les deux, et seule cette règle reste juste pour
 * lui.
 */
import { isFull } from '../places';
import { lundiDe } from '../presences/lien';
import { inscriptionsOuvertes, premierDuMois } from '../seo/stage';
import { estActive } from './statut';

/** Des dates `AAAA-MM-JJ`, comparées comme des chaînes : jamais de fuseau. */
export interface Periode {
  start_date: string;
  end_date: string;
}

/** Vrai si les deux périodes ont au moins un jour en commun. */
export function seRecoupent(a: Periode, b: Periode): boolean {
  return a.start_date <= b.end_date && a.end_date >= b.start_date;
}

/**
 * Vrai si l'inscription tient la place : payée ou en attente de paiement, et
 * pas annulée. C'est ce que `create_order` et l'index
 * `registrations_une_inscription_active` comptent pour la même session.
 */
export function tientLaPlace(
  paymentStatus: string | null | undefined,
  cancellationStatus: string | null | undefined,
): boolean {
  return (paymentStatus === 'paid' || paymentStatus === 'pending') && estActive(cancellationStatus);
}

/* ------------------------------------------------------------------------ */
/*  Ce qui prend une semaine                                                 */
/* ------------------------------------------------------------------------ */

export type SourceOccupant = 'inscription' | 'panier' | 'demande' | 'choix';

/** Une session qui prend (une partie de) la semaine d'un enfant. */
export interface Occupant extends Periode {
  source: SourceOccupant;
  sessionId: string;
  kidId: string;
  titre: string;
  centre: string | null;
  /** Une inscription dont l'annulation est demandée : elle tient encore la place. */
  annulationDemandee?: boolean;
}

const RANG: Record<SourceOccupant, number> = { inscription: 0, panier: 1, demande: 2, choix: 3 };

/** Ce qui bloque la semaine, sans échange possible : tout sauf un choix en cours. */
export const bloque = (o: Occupant) => o.source !== 'choix';

/**
 * Ce qui prend la période pour cet enfant, la session `sauf` exceptée — une
 * carte ne se bloque pas elle-même. Trié : inscription, panier, demande,
 * choix ; puis par date et par titre.
 */
export function occupantsDe(
  periode: Periode,
  kidId: string,
  occupants: readonly Occupant[],
  sauf?: string,
): Occupant[] {
  return occupants
    .filter((o) => o.kidId === kidId && o.sessionId !== sauf && seRecoupent(o, periode))
    .sort(
      (a, b) =>
        RANG[a.source] - RANG[b.source] ||
        a.start_date.localeCompare(b.start_date) ||
        a.titre.localeCompare(b.titre, 'fr'),
    );
}

/** Une inscription lue avec les dates de sa session. */
export interface InscriptionDatee {
  id: string;
  kid_id: string;
  activity_id: string;
  payment_status: string | null;
  cancellation_status: string | null;
  session: SessionDatee | null;
}

/** Ce qu'il faut d'une session pour dire quelle semaine elle prend. */
export interface SessionDatee extends Periode {
  id: string;
  titre: string;
  centre: string | null;
  type_activite: string | null;
  active: boolean | null;
  archived: boolean | null;
}

/** Une session relue juste avant le panier : de quoi savoir si elle se prend encore. */
export interface SessionRelue extends SessionDatee {
  visible_from: string | null;
  capacity: number;
  current_registrations: number | null;
}

/**
 * Une session qui ne prend aucune semaine : retirée du catalogue (archiver une
 * semaine à venir, c'est annuler le stage) ou journée pédagogique, qui n'est
 * pas un stage et s'inscrit par sa propre page.
 */
export function horsDuCatalogue(s: Pick<SessionDatee, 'active' | 'archived' | 'type_activite'>): boolean {
  return s.active === false || s.archived === true || s.type_activite === 'jpa';
}

export function occupantsDesInscriptions(inscriptions: readonly InscriptionDatee[]): Occupant[] {
  return inscriptions.flatMap((i) =>
    i.session && tientLaPlace(i.payment_status, i.cancellation_status) && !horsDuCatalogue(i.session)
      ? [{
          source: 'inscription' as const,
          sessionId: i.activity_id,
          kidId: i.kid_id,
          start_date: i.session.start_date,
          end_date: i.session.end_date,
          titre: i.session.titre,
          centre: i.session.centre,
          annulationDemandee: i.cancellation_status === 'requested',
        }]
      : [],
  );
}

/**
 * Les lignes du panier, ou les demandes, devenues des occupants. Une ligne dont
 * on ne connaît pas encore la session, ou dont la session a quitté le
 * catalogue, ne prend rien : le panier et la base la rattrapent.
 */
export function occupantsDesLignes(
  source: 'panier' | 'demande',
  lignes: readonly { activity_id: string; kid_id: string }[],
  sessions: ReadonlyMap<string, SessionDatee>,
): Occupant[] {
  return lignes.flatMap((l) => {
    const s = sessions.get(l.activity_id);
    if (!s || horsDuCatalogue(s)) return [];
    return [{
      source,
      sessionId: l.activity_id,
      kidId: l.kid_id,
      start_date: s.start_date,
      end_date: s.end_date,
      titre: s.titre,
      centre: s.centre,
    }];
  });
}

/* ------------------------------------------------------------------------ */
/*  Les choix en cours                                                       */
/* ------------------------------------------------------------------------ */

/** Un stage coché pour un enfant, avec ses dates : il survit aux filtres. */
export interface Choix extends Periode {
  cle: string;
  sessionId: string;
  kidId: string;
}

/**
 * Coche `nouveau` et retire les choix du même enfant qui recoupent sa
 * semaine : la sélection ne porte jamais deux stages de la même semaine pour
 * un enfant. Rend aussi ce qui a été retiré, pour le dire au parent.
 */
export function choisir<T extends Choix>(choix: readonly T[], nouveau: T): { choix: T[]; retires: T[] } {
  const retires = choix.filter(
    (c) => c.kidId === nouveau.kidId && c.cle !== nouveau.cle && seRecoupent(c, nouveau),
  );
  const reste = choix.filter((c) => c.cle !== nouveau.cle && !retires.includes(c));
  return { choix: [...reste, nouveau], retires };
}

export function retirer<T extends Choix>(choix: readonly T[], cle: string): T[] {
  return choix.filter((c) => c.cle !== cle);
}

/* ------------------------------------------------------------------------ */
/*  Juste avant le panier                                                    */
/* ------------------------------------------------------------------------ */

export type RaisonDuRefus = 'retire' | 'fermee' | 'complete' | 'deja-inscrit' | 'semaine-prise';

export interface Refus<T> {
  choix: T;
  raison: RaisonDuRefus;
  /** Ce qui prend la semaine, pour `semaine-prise`. */
  par: Occupant[];
}

/**
 * Au clic sur « Ajouter au panier », sur des données RELUES (sessions,
 * inscriptions, panier) : ce qui part au panier, et ce qui ne part pas, avec
 * sa raison. Un choix peut dater d'un autre enfant, d'un autre centre ou d'une
 * autre période — tous partent, aucun n'est laissé de côté sans un mot.
 *
 * `bloquants` : les inscriptions, les lignes du panier et les demandes, pas
 * les choix. Une ligne du panier sur la MÊME session ne refuse rien : le panier
 * ne la double pas.
 */
export function verifierAvantPanier<T extends Choix>(
  choix: readonly T[],
  sessions: ReadonlyMap<string, SessionRelue>,
  bloquants: readonly Occupant[],
  maintenant: Date,
): { ajoutes: T[]; refus: Refus<T>[] } {
  const ajoutes: T[] = [];
  const refus: Refus<T>[] = [];
  for (const c of choix) {
    const s = sessions.get(c.sessionId);
    const memeSession = bloquants.filter(
      (o) => o.kidId === c.kidId && o.sessionId === c.sessionId && o.source === 'inscription',
    );
    const ailleurs = occupantsDe(c, c.kidId, bloquants.filter(bloque), c.sessionId);
    if (!s || horsDuCatalogue(s)) refus.push({ choix: c, raison: 'retire', par: [] });
    else if (memeSession.length > 0) refus.push({ choix: c, raison: 'deja-inscrit', par: memeSession });
    else if (ailleurs.length > 0) refus.push({ choix: c, raison: 'semaine-prise', par: ailleurs });
    else if (!inscriptionsOuvertes(s, maintenant)) refus.push({ choix: c, raison: 'fermee', par: [] });
    else if (isFull(s)) refus.push({ choix: c, raison: 'complete', par: [] });
    else ajoutes.push(c);
  }
  return { ajoutes, refus };
}

/**
 * Ce que le parent lit quand un choix ne part pas au panier, et ce qu'il peut
 * faire. Le titre vient après les deux-points : « Les Hirondelles » n'est pas
 * un sujet singulier. La semaine est nommée — le message ne s'affiche pas
 * sous elle.
 */
export function messageDuRefus(
  titre: string,
  prenom: string,
  r: Pick<Refus<Periode>, 'raison' | 'par' | 'choix'>,
  aujourdHui: string,
): string {
  const semaine = `la semaine ${duAu(r.choix.start_date, r.choix.end_date, aujourdHui)}`;
  switch (r.raison) {
    case 'retire':
      return `Stage non ajouté pour ${prenom} : ${titre} (il n'est plus proposé). Choisissez un autre stage ${semaine}.`;
    case 'fermee':
      return `Stage non ajouté pour ${prenom} : ${titre} (les inscriptions ne sont pas encore ouvertes).`;
    case 'complete':
      return `Stage non ajouté pour ${prenom} : ${titre} (complet depuis votre choix). Choisissez un autre stage ${semaine}, ou rejoignez la liste d'attente.`;
    case 'deja-inscrit':
      return `${prenom} a déjà sa place au stage ${titre}.`;
    case 'semaine-prise':
      return `Stage non ajouté pour ${prenom} : ${titre}. ${prenom} a déjà un stage ${semaine} : ${nommer(r.par, null)}.`;
  }
}

/* ------------------------------------------------------------------------ */
/*  L'état d'une carte                                                       */
/* ------------------------------------------------------------------------ */

/**
 *  - `inchangee` : la carte ne se coche pas, règle ou pas (complet, bientôt,
 *    déjà inscrit, au panier, demande envoyée…) : elle garde son état ;
 *  - `libre` : elle se coche ;
 *  - `choisie` ;
 *  - `remplacable` : un autre choix en cours prend la semaine — « Choisir
 *    celui-ci à la place » ;
 *  - `bloquee` : une inscription, une ligne du panier ou une demande prend la
 *    semaine.
 *
 * Un occupant qui bloque l'emporte sur « choisie » : une ligne du panier lue
 * après coup ne laisse pas un choix incompatible affiché comme valable.
 */
export type EtatDeLaCarte =
  | { etat: 'inchangee' }
  | { etat: 'libre' }
  | { etat: 'choisie' }
  | { etat: 'remplacable'; par: Occupant[] }
  | { etat: 'bloquee'; par: Occupant[] };

export function etatDeLaCarte(
  session: Periode & { id: string },
  kidId: string,
  cochableSansRegle: boolean,
  estChoisie: boolean,
  occupants: readonly Occupant[],
): EtatDeLaCarte {
  if (!cochableSansRegle) return { etat: 'inchangee' };
  const par = occupantsDe(session, kidId, occupants, session.id);
  const bloquants = par.filter(bloque);
  if (bloquants.length > 0) return { etat: 'bloquee', par: bloquants };
  if (estChoisie) return { etat: 'choisie' };
  if (par.length > 0) return { etat: 'remplacable', par };
  return { etat: 'libre' };
}

/* ------------------------------------------------------------------------ */
/*  L'en-tête d'une semaine                                                  */
/* ------------------------------------------------------------------------ */

/** Ce que l'en-tête dit de la semaine, une fois pour toutes ses cartes. */
export type EtatDeLaSemaine =
  | { ton: 'libre'; texte: string }
  | { ton: 'attente'; texte: string }
  | { ton: 'choix'; texte: string; occupant: Occupant }
  | { ton: 'panier'; texte: string; occupant: Occupant }
  | { ton: 'inscription'; texte: string }
  | { ton: 'demande'; texte: string };

/**
 * « Les Hirondelles », « Les Hirondelles et Vélo ». Le centre est ajouté —
 * « Les Hirondelles (Forest) » — quand il n'est pas `ici`, le centre que le
 * parent a sous les yeux ; `null` : tous les centres, il est toujours dit.
 * Sans `ici`, jamais : le même titre existe dans plusieurs centres.
 */
export function nommer(os: readonly { titre: string; centre?: string | null }[], ici?: string | null): string {
  const noms = [
    ...new Set(os.map((o) => (ici !== undefined && o.centre && o.centre !== ici ? `${o.titre} (${o.centre})` : o.titre))),
  ];
  return noms.length <= 1 ? noms.join('') : `${noms.slice(0, -1).join(', ')} et ${noms[noms.length - 1]}`;
}

/**
 * `occupants` : ceux de l'enfant, toutes sources. `compte` : les cartes de la
 * semaine qui se cochent sans la règle, celles pas encore ouvertes et celles
 * complètes. `centreAffiche` : le centre filtré (`null` : tous) — le centre
 * de ce qui prend la semaine est dit s'il diffère, toujours avec « Tous les
 * centres » : le même titre existe dans plusieurs centres.
 */
export function etatDeLaSemaine(
  semaine: Periode,
  kidId: string,
  prenom: string,
  occupants: readonly Occupant[],
  compte: { cochables: number; bientot: number; complets: number },
  centreAffiche: string | null,
): EtatDeLaSemaine {
  const par = occupantsDe(semaine, kidId, occupants);
  const inscriptions = par.filter((o) => o.source === 'inscription');
  if (inscriptions.length > 0) {
    if (inscriptions.length === 1 && inscriptions[0].annulationDemandee) {
      return {
        ton: 'inscription',
        texte: `${prenom} a encore sa place cette semaine : ${nommer(inscriptions, centreAffiche)} (annulation demandée). Vous pourrez choisir un autre stage quand l'équipe l'aura traitée.`,
      };
    }
    return { ton: 'inscription', texte: `${prenom} a déjà sa place cette semaine : ${nommer(inscriptions, centreAffiche)}.` };
  }
  const panier = par.find((o) => o.source === 'panier');
  if (panier) {
    return { ton: 'panier', texte: `Dans votre panier pour ${prenom} : ${nommer([panier], centreAffiche)}.`, occupant: panier };
  }
  const demandes = par.filter((o) => o.source === 'demande');
  if (demandes.length > 0) {
    return { ton: 'demande', texte: `Demande envoyée pour ${prenom} : ${nommer(demandes, centreAffiche)}. L'équipe vous répond par courriel.` };
  }
  const choix = par.find((o) => o.source === 'choix');
  if (choix) return { ton: 'choix', texte: `Choisi : ${nommer([choix], centreAffiche)}`, occupant: choix };
  if (compte.cochables > 0) {
    return { ton: 'libre', texte: `${compte.cochables} stage${compte.cochables > 1 ? 's' : ''} au choix pour ${prenom}` };
  }
  if (compte.bientot > 0) return { ton: 'attente', texte: 'Inscriptions bientôt ouvertes' };
  if (compte.complets > 0) return { ton: 'attente', texte: "Tout est complet cette semaine : la liste d'attente reste possible." };
  return { ton: 'attente', texte: `Pas de stage à choisir cette semaine pour ${prenom}` };
}

/* ------------------------------------------------------------------------ */
/*  Les semaines de la page                                                  */
/* ------------------------------------------------------------------------ */

export interface SemaineDeStages<T> extends Periode {
  /** Le lundi, ou `sejour:<début>:<fin>` pour un séjour de plusieurs semaines. */
  cle: string;
  sejour: boolean;
  /** Le nombre de jours, quand toutes ses sessions ont le même ; sinon `null`. */
  jours: number | null;
  sessions: T[];
}

/** L'ordre des cartes dans une semaine : les plaines, puis les stages, puis le reste. */
const RANG_DU_TYPE: Record<string, number> = { plaine: 0, stage: 1, sejour: 2 };

/**
 * Range les sessions par semaine du calendrier (le lundi de leur début), dans
 * l'ordre des dates. Une session qui court sur plusieurs semaines — un séjour —
 * forme son propre bloc, placé après la dernière semaine qu'elle couvre.
 * Dans un bloc, l'ordre ne bouge jamais : type, titre, puis identifiant.
 */
export function grouperParSemaine<T extends Periode & { id: string }>(
  sessions: readonly T[],
  lire: (s: T) => { titre: string; type: string | null; jours: number | null },
): SemaineDeStages<T>[] {
  const blocs = new Map<string, { tri: string; sejour: boolean; sessions: T[] }>();
  for (const s of sessions) {
    const lundi = lundiDe(s.start_date);
    const sejour = lundiDe(s.end_date) !== lundi;
    const cle = sejour ? `sejour:${s.start_date}:${s.end_date}` : lundi;
    // « ~ » se range après les chiffres : le séjour suit la semaine de sa fin.
    const tri = sejour ? `${lundiDe(s.end_date)}~${s.start_date}` : lundi;
    const bloc = blocs.get(cle) ?? { tri, sejour, sessions: [] };
    bloc.sessions.push(s);
    blocs.set(cle, bloc);
  }

  return [...blocs.entries()]
    .sort(([, a], [, b]) => a.tri.localeCompare(b.tri))
    .map(([cle, b]) => {
      const tries = [...b.sessions].sort((x, y) => {
        const lx = lire(x);
        const ly = lire(y);
        return (
          (RANG_DU_TYPE[lx.type ?? ''] ?? 3) - (RANG_DU_TYPE[ly.type ?? ''] ?? 3) ||
          lx.titre.localeCompare(ly.titre, 'fr') ||
          x.id.localeCompare(y.id)
        );
      });
      const jours = new Set(tries.map((s) => lire(s).jours));
      const [seul] = [...jours];
      return {
        cle,
        sejour: b.sejour,
        start_date: tries.reduce((m, s) => (s.start_date < m ? s.start_date : m), tries[0].start_date),
        end_date: tries.reduce((m, s) => (s.end_date > m ? s.end_date : m), tries[0].end_date),
        jours: jours.size === 1 && typeof seul === 'number' ? seul : null,
        sessions: tries,
      };
    });
}

/* ------------------------------------------------------------------------ */
/*  Les dates en toutes lettres                                              */
/* ------------------------------------------------------------------------ */

/** Lue à midi UTC : la date du jour, sans fuseau qui la décale. */
const aMidi = (iso: string) => new Date(`${iso}T12:00:00Z`);

const format = (iso: string, options: Intl.DateTimeFormatOptions) =>
  premierDuMois(new Intl.DateTimeFormat('fr-BE', { ...options, timeZone: 'UTC' }).format(aMidi(iso)));

/**
 * « du 21 au 25 décembre », « du 30 novembre au 4 décembre », « du 28 décembre
 * au 1er janvier 2027 ». L'année n'est écrite que si elle n'est pas celle
 * d'`aujourdHui` (`AAAA-MM-JJ`, la date de Bruxelles) ; le début la porte
 * aussi s'il tombe une autre année que la fin.
 */
export function duAu(debut: string, fin: string, aujourdHui: string): string {
  const annee = (iso: string) => iso.slice(0, 4);
  const anneeFin = annee(fin) !== annee(aujourdHui);
  const anneeDebut = annee(debut) !== annee(fin) && annee(debut) !== annee(aujourdHui);
  const memeMois = debut.slice(0, 7) === fin.slice(0, 7);
  const debutTexte = memeMois
    ? format(debut, { day: 'numeric' })
    : format(debut, { day: 'numeric', month: 'long', ...(anneeDebut ? { year: 'numeric' } : {}) });
  const finTexte = format(fin, { day: 'numeric', month: 'long', ...(anneeFin ? { year: 'numeric' } : {}) });
  return `du ${debutTexte} au ${finTexte}`;
}

/** « Semaine du 21 au 25 décembre », ou « Séjour du 21 décembre au 1er janvier 2027 ». */
export function enTeteDeSemaine(semaine: Periode & { sejour?: boolean }, aujourdHui: string): string {
  return `${semaine.sejour ? 'Séjour' : 'Semaine'} ${duAu(semaine.start_date, semaine.end_date, aujourdHui)}`;
}

/**
 * Les dates d'une carte, seulement quand elles diffèrent de celles de sa
 * semaine : « Du mardi 22 au vendredi 25 décembre ». Sinon `null` — l'en-tête
 * les dit déjà.
 */
export function datesDeLaCarte(session: Periode, semaine: Periode): string | null {
  if (session.start_date === semaine.start_date && session.end_date === semaine.end_date) return null;
  const memeMois = session.start_date.slice(0, 7) === session.end_date.slice(0, 7);
  const debut = format(session.start_date, memeMois ? { weekday: 'long', day: 'numeric' } : { weekday: 'long', day: 'numeric', month: 'long' });
  const fin = format(session.end_date, { weekday: 'long', day: 'numeric', month: 'long' });
  return `Du ${debut} au ${fin}`;
}

/** La date du jour à Bruxelles, `AAAA-MM-JJ`. */
export function aujourdHuiABruxelles(maintenant: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Brussels' }).format(maintenant);
}
